import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PublicQuizAPI, NextQuestionResult, QuestionOption, ProgressResult, ScoreRegressionWarning } from '../../apis/PublicQuizAPI'
import bg1 from '../../images/bg-1.png'
import { qfonts } from './quizTheme'

interface Props {
  attemptId: string
  isResuming: boolean
  assessmentName?: string
  questionCap?: number | null
  initialQuestionCount?: number
  onQuestionCount?: (count: number) => void
  onComplete: () => void
  onRestart: () => void
  domain?: string
}

type CardPhase = 'idle' | 'exiting' | 'entering'

const LEVEL_MAP: Record<string, { num: number; color: string; bg: string }> = {
  Basic:             { num: 1, color: '#7ba8ff', bg: 'rgba(64,97,194,0.55)' },
  Intermediate:      { num: 2, color: '#4fe0eb', bg: 'rgba(0,120,129,0.55)' },
  Advanced:          { num: 3, color: '#c4a8ff', bg: 'rgba(137,103,200,0.55)' },
  'Highly advanced': { num: 4, color: '#ffb86b', bg: 'rgba(255,130,0,0.55)' },
}
const scoreToLevel = (s: number) =>
  s < 2 ? 'Basic' : s < 3 ? 'Intermediate' : s < 4 ? 'Advanced' : 'Highly advanced'

export default function QuizPage({ attemptId, isResuming, assessmentName, questionCap = null, initialQuestionCount = 0, onQuestionCount, onComplete, onRestart, domain }: Props) {
  const adminMode = useMemo(() => (new URLSearchParams(window.location.search).get('admin') ?? '') !== '', [])
  const autoMode  = useMemo(() => new URLSearchParams(window.location.search).get('auto')  === '1', [])
  const [question, setQuestion] = useState<NextQuestionResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [selected, setSelected] = useState<number | null>(null)
  const [, setAdminVerdict] = useState<boolean | null>(null)
  const [questionCount, setQuestionCount] = useState(Math.max(0, initialQuestionCount - 1))
  const [showResumeBanner, setShowResumeBanner] = useState(isResuming)
  const [cardPhase, setCardPhase] = useState<CardPhase>('idle')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startTimeRef = useRef(Date.now())
  const [progress, setProgress] = useState<ProgressResult | null>(null)
  const currentCellRef = useRef<HTMLSpanElement | null>(null)
  const [regressionToast, setRegressionToast] = useState<ScoreRegressionWarning[] | null>(null)

  // Scroll the active level cell into view once progress has updated for the new question
  useEffect(() => {
    currentCellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [progress])

  // Propagate question count to parent as a side-effect, not inside a setState updater
  useEffect(() => {
    onQuestionCount?.(questionCount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionCount])

  // Auto-submit a random answer when ?auto=1 is set
  useEffect(() => {
    if (!autoMode || !question) return
    const timer = setTimeout(() => {
      handleAdminSubmit(Math.random() > 0.5)
    }, 400)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.question._id, autoMode])

  const loadNext = useCallback(async () => {
    try {
      const next = await PublicQuizAPI.nextQuestion(attemptId, new URLSearchParams(window.location.search).get('admin'), autoMode)
      if (!next) {
        // Mark attempt complete and trigger PDF email
        await PublicQuizAPI.complete(attemptId, domain ?? process.env.REACT_APP_REPORT_DOMAIN).catch((err) =>
          console.error('[QuizPage] complete failed', err),
        )
        onComplete()
        return
      }
      // Swap content while card is invisible (opacity 0), no height flicker
      setQuestion(next)
      setSelected(null)
      setAdminVerdict(null)
      PublicQuizAPI.getProgress(attemptId).then(setProgress).catch(() => {})
      setQuestionCount((c) => c + 1)
      // snap to right side instantly (no transition), then slide in
      setCardPhase('entering')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setCardPhase('idle'))
      })
    } catch (err) {
      console.error('[QuizPage] failed to load next question', err)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, autoMode, onComplete])

  useEffect(() => {
    loadNext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextLockRef = useRef(false)

  const handleNext = useCallback(async () => {
    if (selected === null || nextLockRef.current || !question) return
    nextLockRef.current = true
    setSubmitting(true)

    const selectedKey = question.question.options[selected]?.key
    const selectedOptionKeys = selectedKey ? [selectedKey] : []
    const isCorrect = question.question.answerIndex === selected
    try {
      const result = await PublicQuizAPI.submitAnswer(attemptId, question.question._id, selectedOptionKeys)
      if (adminMode && isCorrect && result.regressions.length > 0) {
        setRegressionToast(result.regressions)
      }
    } catch (err) {
      console.error('[QuizPage] submitAnswer failed', err)
    }
    PublicQuizAPI.getProgress(attemptId).then(setProgress).catch(() => {})

    // slide out to left; card is invisible during fetch — no skeleton swap
    setCardPhase('exiting')
    setTimeout(() => {
      setSubmitting(false)
      nextLockRef.current = false
      loadNext()
    }, 320)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, question, attemptId, loadNext])

  const handleAdminSubmit = useCallback(async (correct: boolean) => {
    if (nextLockRef.current || !question) return
    setAdminVerdict(correct)
    nextLockRef.current = true
    setSubmitting(true)
    // In admin mode the correct option key is known via answerIndex
    const correctKey = question.question.options[question.question.answerIndex]?.key
    const wrongKey = question.question.options.find((option) => option.key !== correctKey)?.key
    const selectedOptionKeys = correct
      ? (correctKey ? [correctKey] : [])
      : (wrongKey ? [wrongKey] : [])
    try {
      const result = await PublicQuizAPI.submitAnswer(attemptId, question.question._id, selectedOptionKeys)
      if (correct && result.regressions.length > 0) {
        setRegressionToast(result.regressions)
      }
    } catch (err) {
      console.error('[QuizPage] adminSubmit failed', err)
    }
    PublicQuizAPI.getProgress(attemptId).then(setProgress).catch(() => {})
    // Delay exiting so the green/red verdict colour is visible before fade-out
    await new Promise((r) => setTimeout(r, 500))
    setCardPhase('exiting')
    setTimeout(() => {
      setSubmitting(false)
      nextLockRef.current = false
      loadNext()
    }, 320)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, attemptId, loadNext])

  const cardStyle: CSSProperties = {
    // Avoid transform animations on the frosted panel; backdrop blur can
    // visibly re-rasterize and appear to shrink during the phase transition.
    transition: 'opacity 0.32s ease',
    opacity: cardPhase === 'idle' ? 1 : 0,
    transform: 'translateX(0)',
  }

  // ─── Initial loading screen ───────────────────────────────────────────────

  if (loading && questionCount === 0) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: "linear-gradient(rgba(0,0,0,0.80), rgba(0,0,0,0.80)), url('/images/hero-banner-naked.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin w-8 h-8 text-brand-yellow" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-white/50 text-sm uppercase tracking-widest">Loading…</span>
        </div>
      </div>
    )
  }

  const options = question?.question.options ?? []
  const activeCompCode = question?.competenceId?.replace('Competence/', '') ?? null

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden overscroll-none">
      {/* ── Resume banner ── */}
      {showResumeBanner && (
        <div className="bg-brand-yellow">
          <div className={`max-w-2xl mx-auto px-6 py-3 flex items-center gap-3 text-sm text-black font-medium ${qfonts.body}`}>
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <span>Resuming your previous session — picking up where you left off.</span>
            <button onClick={() => setShowResumeBanner(false)} className="ml-auto">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Hero banner ── */}
      <div
        className="flex-shrink-0 w-full relative overflow-hidden"
        style={{
          backgroundImage: "linear-gradient(rgba(0,0,0,0.62), rgba(0,0,0,0.62)), url('/images/hero-banner-naked.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between gap-6">
          {/* Left: title + subtitle */}
          <div className="flex flex-col gap-1 min-w-0">
            <h1 className="text-white font-normal text-[32px] leading-tight font-quiz-heading">
              {assessmentName || 'Self-Assessment'}
            </h1>
          </div>
          {/* Right: question counter */}
          <div className="flex-shrink-0 flex flex-col items-end gap-1">
            <span className={`text-white text-xs tracking-widest ${qfonts.body}`}>Question</span>
            <span className={`text-brand-yellow font-black text-2xl leading-none tabular-nums ${qfonts.body}`}>
              {questionCount}<span className="text-white font-normal text-sm"> / {questionCap ?? '?'}</span>
            </span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="px-6 pb-4 max-w-3xl mx-auto w-full">
          <div className="w-full h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-yellow rounded-full"
              style={{ width: questionCount > 0 ? `${Math.min(questionCount / (questionCap ?? 40) * 100, 100)}%` : '2%', transition: 'width 0.6s ease' }}
            />
          </div>
        </div>
        {/* Yellow accent line at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black" />
      </div>

      {/* ── Body: sidebar + main ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Competence sidebar (admin mode only) ── */}
        {adminMode && (
        <aside className="w-[30rem] bg-neutral-950 flex-shrink-0 flex flex-col border-r border-white/10">
          {/* Global KSA + Overall scores bar */}
          {progress && (() => {
            const hasAny = progress.areas.some(a => a.competences.some(c => c.directAnswerCount > 0))
            if (!hasAny) return null
            const fmt = (s: number | null) => s == null ? '·' : s.toFixed(2)
            const lvlLabel = (s: number | null) => s == null ? null : scoreToLevel(s)
            return (
              <div className="flex gap-1 px-3 pt-2 pb-2 border-b border-white/10 flex-shrink-0">
                {([
                  { label: 'Overall', score: progress.overallScore ?? null },
                  { label: 'K', score: progress.kScore ?? null },
                  { label: 'S', score: progress.sScore ?? null },
                  { label: 'A', score: progress.aScore ?? null },
                ] as const).map(({ label, score }) => {
                  const lm = score !== null ? LEVEL_MAP[scoreToLevel(score)] : null
                  return (
                    <div key={label} className="flex-1 flex flex-col items-center rounded bg-white/5 px-1 py-1 gap-0.5">
                      <span className="text-[9px] font-bold uppercase tracking-wide text-white/40">{label}</span>
                      {lm ? (
                        <>
                          <span className="text-[11px] font-bold font-mono leading-none" style={{ color: lm.color }}>{fmt(score)}</span>
                          <span className="text-[9px] font-mono text-white/40">{lvlLabel(score)}</span>
                        </>
                      ) : (
                        <span className="text-[10px] text-white/20">·</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })()}
          {/* Competence list */}
          <div className="flex-1 overflow-y-auto py-1">
            {progress?.areas.map((area) => {
              const hasAreaData = area.score !== null && area.score !== undefined
              const areaLm = hasAreaData ? LEVEL_MAP[scoreToLevel(area.score!)] : null
              return (
              <div key={area.areaCode} className="mb-1">
                {/* Area header row — same structure as comp rows */}
                <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                  <span className={`w-20 flex-shrink-0 text-sm font-bold ${hasAreaData ? 'text-white/90' : 'text-white/25'}`}>Area {area.areaCode}</span>
                  {hasAreaData && areaLm ? (
                    <>
                      <span
                        className="flex-shrink-0 text-[11px] font-bold font-mono rounded px-1.5 py-0.5 leading-none"
                        style={{ color: areaLm.color, background: areaLm.bg }}
                      >
                        {area.score!.toFixed(2)}
                      </span>
                      <span className="text-[11px] font-mono text-white/40">{Math.round(area.confidence * 100)}%</span>
                    </>
                  ) : null}
                </div>
                {area.competences.map((comp) => {
                  const isActive = comp.code === activeCompCode
                  const lm = LEVEL_MAP[comp.estimatedLevel]
                  const hasData = comp.directAnswerCount > 0
                  return (
                    <div key={comp.code} className="mx-1 mb-px">
                      {/* Competence summary row */}
                      <div
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm font-mono transition-colors ${
                          isActive ? 'bg-brand-yellow/10' : 'hover:bg-white/5'
                        }`}
                        style={isActive ? { borderLeft: '2px solid #ffdb00' } : { borderLeft: '2px solid transparent' }}
                      >
                        <span className={`w-20 flex-shrink-0 text-xs ${isActive ? 'text-brand-yellow' : hasData ? 'text-white/70' : 'text-white/25'}`}>
                          Comp {comp.code}
                        </span>
                        {hasData && lm ? (
                          <>
                            <span
                              className="flex-shrink-0 text-[11px] font-bold font-mono rounded px-1.5 py-0.5 leading-none"
                              style={{ color: lm.color, background: lm.bg }}
                            >
                              {comp.score != null ? comp.score.toFixed(2) : lm.num}
                            </span>
                            <span className="text-[11px] font-mono text-white/40">{Math.round(comp.confidence * 100)}%</span>
                          </>
                        ) : (
                          <span className="text-white/30 text-xs">·</span>
                        )}
                      </div>

                          {comp.types && comp.types.length > 0 && (
                          <div className="pl-2 pr-1 pb-1.5 space-y-0.5">
                            {comp.types.map((td) => {
                              const typeAbbr = ({ Knowledge: 'K', Skill: 'S', Attitude: 'A' } as Record<string, string>)[td.type] ?? td.type[0]
                              const isInferredType = (td as any).isInferred === true
                              const hasType = td.directAnswerCount > 0 || isInferredType
                              const typeLm = hasType ? LEVEL_MAP[td.estimatedLevel] : null
                              return (
                                <div key={td.type} className="flex items-center gap-1.5 px-1">
                                  {/* label + score + confidence — fixed width so level cells align */}
                                  <span className={`text-[10px] font-bold w-4 flex-shrink-0 ${td.directAnswerCount > 0 ? 'text-brand-yellow' : hasType ? 'text-white/50' : 'text-white/25'}`}>{typeAbbr}</span>
                                  {hasType && typeLm ? (
                                    <span className="text-[10px] font-mono w-16 flex-shrink-0" style={{ color: isInferredType ? 'rgba(255,255,255,0.4)' : typeLm.color }}>
                                      {td.score != null ? td.score.toFixed(2) : typeLm.num}{' '}
                                      <span className="text-white/40">{isInferredType ? '~' : `${Math.round(td.confidence * 100)}%`}</span>
                                    </span>
                                  ) : (
                                    <span className="text-white/20 text-[10px] w-16 flex-shrink-0">·</span>
                                  )}
                                  {/* Per-level belief cells */}
                                  <div className="flex-1 flex gap-1">
                                    {td.levels.map((lb) => {
                                      const hasEvidence = td.directAnswerCount > 0 || isInferredType
                                      const isEst = hasEvidence && lb.level === td.estimatedLevel
                                      const isNextProbe = lb.level === td.nextProbeLevel
                                      const isCurrent =
                                        question?.competenceId === comp.competenceId &&
                                        question?.outcomeType === td.type &&
                                        question?.level === lb.level
                                      return (
                                        <span
                                          key={lb.level}
                                          ref={isCurrent ? currentCellRef : undefined}
                                          className={`relative flex-1 text-center text-[10px] font-mono tabular-nums rounded py-0.5 leading-tight ${
                                            isEst ? 'text-white font-bold' : 'text-white/40'
                                          }`}
                                          style={{
                                            background: isEst ? 'rgba(255,219,0,0.18)' : 'rgba(255,255,255,0.05)',
                                            outline: isCurrent
                                              ? '2px solid rgba(255,219,0,0.9)'
                                              : isNextProbe
                                              ? '1px solid rgba(255,255,255,0.35)'
                                              : !lb.hasLo
                                              ? '1px dashed rgba(255,255,255,0.55)'
                                              : undefined,
                                            outlineOffset: '1px',
                                          }}
                                        >
                                          <div>{lb.p.toFixed(2)}</div>
                                          <div className={`text-[9px] ${isEst ? 'text-white/80' : 'text-white/50'}`}>{lb.alpha}/{lb.beta}</div>
                                          {lb.aiExplicitCount > 0 && (
                                            <span className="absolute bottom-0 left-0 text-[7px] font-bold text-emerald-400 leading-none px-px">ai:{lb.aiExplicitCount}</span>
                                          )}
                                        </span>
                                      )
                                    })}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                    </div>
                  )
                })}
              </div>
              )
            })}
            {!progress && (
              <div className="px-3 py-4 text-xs text-white/40 text-center">Loading…</div>
            )}
          </div>
        </aside>
        )}

        {/* ── Main content ── */}
        <div
          className="relative flex-1 overflow-hidden"
          style={{
            backgroundImage: `url(${bg1})`,
            backgroundSize: 'auto 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'left center',
          }}
        >
          <main className={`h-full overflow-y-auto relative${adminMode ? ' pb-16' : ''}`}>
          <button
            onClick={onRestart}
            className="sticky top-4 ml-4 z-10 inline-flex items-center gap-1.5 text-xs text-gray-700 hover:text-black transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
            Back to home page
          </button>
          <div className="min-h-full flex items-center justify-center px-4 py-10 -mt-8">
          <div className="w-full max-w-3xl">

            <div
              className="rounded-lg shadow-2xl border border-white/40 overflow-hidden"
              style={{ backgroundColor: 'rgba(245, 245, 247, 0.55)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
            >
              <div className="w-full" style={cardStyle}>
                {/* Admin metadata bar */}
                {adminMode && question && (
                  <div className="px-4 pt-3 pb-1 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-mono bg-black/5 rounded px-2 py-0.5 text-gray-600">
                      {question.competenceId.replace('Competence/', 'Comp ')}
                    </span>
                    <span className="text-[11px] font-mono bg-black/5 rounded px-2 py-0.5 text-gray-600">
                      {question.outcomeType}
                    </span>
                    <span
                      className="text-[11px] font-mono font-bold rounded px-2 py-0.5 leading-none"
                      style={(() => {
                        const palette: Record<string, { color: string; bg: string }> = {
                          Basic:             { color: '#2a55c7', bg: 'rgba(42,85,199,0.10)' },
                          Intermediate:      { color: '#0a7a83', bg: 'rgba(10,122,131,0.10)' },
                          Advanced:          { color: '#6b3dbf', bg: 'rgba(107,61,191,0.10)' },
                          'Highly advanced': { color: '#b85c00', bg: 'rgba(184,92,0,0.10)' },
                        }
                        const p = palette[question.level] ?? { color: '#555', bg: 'rgba(0,0,0,0.05)' }
                        return { color: p.color, background: p.bg }
                      })()}
                    >
                      {question.level}
                    </span>
                    {question.question.aiLabel && question.question.aiLabel !== 'AI not Implicit or Explicit' && (
                      <span className="text-[11px] font-mono font-bold rounded px-2 py-0.5 text-emerald-600 bg-emerald-50">
                        {question.question.aiLabel}
                      </span>
                    )}
                  </div>
                )}
                {/* Question text */}
                <div className="px-7 pt-7 pb-3">
                  <p className={`text-black font-semibold text-base leading-relaxed whitespace-pre-wrap ${qfonts.body}`}>
                    {question?.question.title.replace(/\\n/g, '\n')}
                  </p>
                </div>

                {/* Options */}
                <div className="px-4 pt-3 pb-4 space-y-2.5">
                  {options.map((opt, i) => {
                    const isSelected = selected === i

                    const isCorrectOption = adminMode && i === question?.question.answerIndex
                    // In admin mode, always reveal the correct option in green;
                    // if the wrong option was selected, show it in red too.
                    const wrapCls = isSelected
                      ? adminMode
                        ? isCorrectOption
                          ? 'border-2 border-green-500 bg-green-50 text-black'
                          : 'border-2 border-red-500 bg-red-50 text-black'
                        : 'border-2 border-brand-yellow bg-brand-yellow/10 text-black'
                      : 'border-2 border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 text-black cursor-pointer'
                    const badgeCls = isSelected
                      ? adminMode
                        ? isCorrectOption
                          ? 'bg-green-500 text-white border-0'
                          : 'bg-red-500 text-white border-0'
                        : 'bg-brand-yellow text-black border-0'
                      : adminMode && isCorrectOption
                        ? 'bg-green-500 text-white border-0'
                        : 'bg-gray-100 text-gray-500 border border-gray-200'

                    return (
                      <button
                        key={i}
                        onClick={() => !submitting && setSelected(i)}
                        className={`w-full flex items-center gap-4 px-5 py-4 text-left text-sm font-medium transition-all duration-200 ${qfonts.body} ${wrapCls}`}
                      >
                        <span
                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-colors duration-200 ${badgeCls}`}
                        >
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span className={`${qfonts.body} whitespace-pre-wrap`}>{(opt as QuestionOption).text.replace(/\\n/g, '\n')}</span>
                      </button>
                    )
                  })}
                </div>

                {/* Next button — always rendered, fixed height to prevent any reflow */}
                <div className="px-4 pb-4 flex items-center justify-center gap-2">
                  <button
                    onClick={handleNext}
                    disabled={submitting || selected === null}
                    className="w-40 h-12 border flex items-center justify-between px-4 bg-brand-yellow hover:brightness-95 active:scale-[0.99] transition-all disabled:opacity-50"
                    style={{ opacity: selected !== null ? 1 : 0, pointerEvents: selected !== null ? 'auto' : 'none', color: 'rgb(26, 30, 43)', borderColor: 'rgb(26, 30, 43)', fontFamily: 'RobotoMono, Arial, sans-serif', fontSize: 16, fontWeight: 400 }}
                  >
                    {submitting ? (
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <>
                        Next
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
          </main>

          {/* Admin verdict bar — pinned to bottom of content area */}
          {adminMode && (
            <div className="absolute bottom-0 left-0 right-0 z-40 flex items-center justify-center gap-3 px-6 py-3">
              <button
                onClick={() => handleAdminSubmit(false)}
                disabled={submitting}
                className="w-28 h-11 flex items-center justify-center gap-1.5 rounded-xl bg-red-500 text-white font-bold text-sm tracking-wide hover:bg-red-600 active:scale-[0.99] transition-all disabled:opacity-40"
              >
                {submitting ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : '\u2717 Wrong'}
              </button>
              <button
                onClick={() => handleAdminSubmit(true)}
                disabled={submitting}
                className="w-28 h-11 flex items-center justify-center gap-1.5 rounded-xl bg-green-500 text-white font-bold text-sm tracking-wide hover:bg-green-600 active:scale-[0.99] transition-all disabled:opacity-40"
              >
                {submitting ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : '\u2713 Correct'}
              </button>
            </div>
          )}
        </div>

      </div>

      <footer className="text-center py-4 text-xs text-gray-700">
        &copy; {new Date().getFullYear()} UNSW. All rights reserved.
      </footer>



      {/* ── Regression toast (admin debug: correct answer lowered a score) ── */}
      {adminMode && regressionToast && (
        <div
          className="fixed bottom-6 right-6 z-50 max-w-sm w-full pointer-events-auto"
          aria-live="polite"
        >
          <div className="bg-red-950 border border-red-500 rounded-xl px-4 py-3 shadow-2xl">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-red-400 font-bold text-xs uppercase tracking-wider">⚠️ Score regression (correct) — {regressionToast.length}</span>
              <button
                onClick={() => setRegressionToast(null)}
                className="text-red-300/60 hover:text-red-200 flex-shrink-0"
                aria-label="Dismiss"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            {/* LO that triggered — same for all entries */}
            <div className="text-white text-xs font-mono mb-2">
              <span className="text-red-300">LO:</span> {regressionToast[0].testedLoCode}
              {'  '}
              <span className="text-red-300">Tested:</span>{' '}
              {regressionToast[0].testedCompetenceId.replace('Competence/', '')} · {regressionToast[0].testedType} · {regressionToast[0].testedLevel}
            </div>
            <div className="flex flex-col gap-1">
              {regressionToast.map((w, i) => {
                const code = w.competenceId.replace('Competence/', '')
                return (
                  <div key={i} className="text-white text-xs font-mono border-t border-red-800/60 pt-1">
                    <div><span className="text-red-300">Affected:</span> {code} · {w.type} &nbsp; <span className="text-red-300">Level:</span> {['Basic','Intermediate','Advanced','Highly advanced'][w.prevScore]} → <span className="text-red-400 font-bold">{['Basic','Intermediate','Advanced','Highly advanced'][w.newScore]}</span></div>
                    <div><span className="text-red-300">α before:</span> [{w.prevAlphas.map((a) => a.toFixed(2)).join(', ')}]</div>
                    <div><span className="text-red-300">α after: </span> [{w.newAlphas.map((a) => a.toFixed(2)).join(', ')}]</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

